import { useState, useRef, useEffect } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Sparkles, Send, Loader2, FileText } from "lucide-react";

const SUGGESTIONS = [
  "Which tie-up type converts best to referrals?",
  "Show me all overdue follow-ups this week",
  "Compare Pavan and Sourabh this month",
  "What's my revenue forecast for this month?",
  "Which partnerships are going cold?",
];

export default function AIAssistant() {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  const send = async (q) => {
    const question = (q ?? input).trim();
    if (!question) return;
    setMessages((m) => [...m, { role: "user", text: question }]);
    setInput("");
    setLoading(true);
    try {
      const r = await api.post("/ai/query", { question });
      setMessages((m) => [...m, { role: "assistant", text: r.data.answer }]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI call failed");
    } finally { setLoading(false); }
  };

  const drawSummary = async () => {
    setSummaryLoading(true);
    try {
      const r = await api.post("/ai/monthly-summary");
      setMessages((m) => [...m, { role: "assistant", text: "**Monthly Summary**\n\n" + r.data.summary }]);
    } catch { toast.error("Summary failed"); }
    finally { setSummaryLoading(false); }
  };

  const canDraftSummary = user?.role === "founder" || user?.role === "manager";

  return (
    <div className="space-y-6 max-w-5xl" data-testid="ai-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] tracking-[0.25em] uppercase text-[#C9A24B]">Intelligence</div>
          <h2 className="text-3xl font-light text-white mt-1 flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
            <Sparkles className="text-[#C9A24B]" /> Field Marketing AI Assistant
          </h2>
          <p className="text-slate-400 text-sm mt-1">
            Powered by Claude Sonnet 4.6 — grounded in your live CRM data.
          </p>
        </div>
        {canDraftSummary && (
          <button data-testid="btn-monthly-summary" onClick={drawSummary} disabled={summaryLoading}
            className="bg-slate-800 hover:bg-slate-700 border border-[#C9A24B]/30 text-[#C9A24B] rounded-lg px-4 py-2 text-sm flex items-center gap-2">
            {summaryLoading ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />}
            Draft Monthly Summary
          </button>
        )}
      </div>

      <div className="bg-[#1E293B] border border-slate-800 rounded-xl overflow-hidden flex flex-col" style={{ height: "62vh" }}>
        <div ref={scrollRef} className="flex-1 overflow-y-auto p-6 space-y-4">
          {messages.length === 0 && (
            <div className="text-slate-400 text-sm">
              Ask anything about your team&apos;s field marketing performance. Try a suggestion below to get started.
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm whitespace-pre-wrap ${
                m.role === "user"
                  ? "bg-[#C9A24B]/15 text-white border border-[#C9A24B]/25"
                  : "bg-slate-800/70 text-slate-100 border border-slate-700"
              }`}>
                {m.text}
              </div>
            </div>
          ))}
          {loading && (
            <div className="flex items-center gap-2 text-slate-400 text-sm">
              <Loader2 size={14} className="animate-spin" /> Analysing your data…
            </div>
          )}
        </div>

        <div className="border-t border-slate-800 p-4">
          <div className="flex flex-wrap gap-2 mb-3">
            {SUGGESTIONS.map((s) => (
              <button key={s} data-testid={`ai-suggest-${s.slice(0, 12)}`} onClick={() => send(s)} disabled={loading}
                className="text-xs bg-slate-800/60 hover:bg-slate-700 border border-slate-700 text-slate-300 rounded-full px-3 py-1.5">
                {s}
              </button>
            ))}
          </div>
          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex items-center gap-2">
            <input
              data-testid="ai-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about your team, tie-ups, revenue, forecasts…"
              className="flex-1 bg-slate-900 border border-slate-700 text-white rounded-lg px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#C9A24B]"
              disabled={loading}
            />
            <button data-testid="ai-send" type="submit" disabled={loading || !input.trim()}
              className="bg-[#C9A24B] hover:bg-[#b08d40] disabled:opacity-50 text-[#0F172A] font-semibold rounded-lg px-4 py-2.5 flex items-center gap-2">
              <Send size={15} /> Ask
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
